/**
 * Did this visitor arrive carrying a credential this app cannot use?
 *
 * ⚠️ **Extracted rather than inlined, for the reason `revealSection.ts` was.** The condition is a
 * property of the URL a person arrived on, and neither a test renderer nor a browser pane reproduces
 * "someone opened a link that another browser requested". A pure function can be asserted directly.
 *
 * ── What this detects ──────────────────────────────────────────────────────────────────────
 *
 * Supabase issues an **implicit-flow** link — `#access_token=…&refresh_token=…` — when the request
 * did not originate in this browser: a link forwarded to a colleague, opened on a phone, or asked
 * for by a script. ADR-069's session is Worker-mediated and accepts only the PKCE `?code=`, which is
 * correct and deliberate: an access token sitting in a fragment is exactly what that design keeps
 * out of the browser.
 *
 * ⚠️ **The refusal was already right; the silence was the defect.** The app ignored such a URL and
 * rendered a plain sign-in page, so someone who had just clicked a link they were sent had no reason
 * to think anything had happened at all. Found by signing in live, not by any test.
 */
export function carriesUnusableCredential(hash: string): boolean {
  if (!hash) return false;
  /* On the `=`, not the bare word: `#access_token=…` is a credential, while a route like
     `#/help?topic=access_tokens` is not, and matching the latter would show a confusing notice to
     someone who simply navigated. */
  return /(^|[#&])access_token=/.test(hash);
}
